import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api } from "../../../convex/_generated/api";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "convex/react";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const createWantSchema = z.object({
  title: z.string().min(3, "Title must be at least 3 characters"),
  description: z.string().optional(),
  minPrice: z.number().min(0).optional(),
  maxPrice: z.number().min(0).optional(),
  urgency: z.enum(["low", "medium", "high"]),
});

type CreateWantFormValues = z.infer<typeof createWantSchema>;

interface CreateWantDialogProps {
  initialQuery?: string;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function CreateWantDialog({
  initialQuery = "",
  trigger,
  open,
  onOpenChange,
}: CreateWantDialogProps) {
  const { t } = useTranslation();
  const createWantedItem = useMutation(api.wanted.createWantedItem);
  const [isOpen, setIsOpen] = useState(false);

  // Handle controlled vs uncontrolled state
  const showDialog = open !== undefined ? open : isOpen;
  const setShowDialog = onOpenChange || setIsOpen;

  const form = useForm<CreateWantFormValues>({
    resolver: zodResolver(createWantSchema),
    defaultValues: {
      title: initialQuery,
      description: "",
      urgency: "medium",
    },
  });

  const onSubmit = async (data: CreateWantFormValues) => {
    try {
      await createWantedItem({
        title: data.title,
        description: data.description,
        minPrice: data.minPrice,
        maxPrice: data.maxPrice,
        urgency: data.urgency,
        // Location/Country could be added here if available in context
      });

      toast.success(t('wants.created'), {
        description: t('wants.createdDesc'),
      });
      setShowDialog(false);
      form.reset();
    } catch (error) {
      toast.error(t('common.error'), {
        description: t('common.tryAgain'),
      });
    }
  };

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('wants.createTitle')}</DialogTitle>
          <DialogDescription>
            {t('wants.createDescription')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('wants.itemTitle')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('wants.placeholderTitle')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('common.description')}</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder={t('wants.placeholderDesc')} 
                      className="resize-none" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex gap-4">
              <FormField
                control={form.control}
                name="minPrice"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>{t('filter.minPrice')}</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="0" 
                        {...field}
                        onChange={(e) => field.onChange(e.target.valueAsNumber)} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="maxPrice"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>{t('filter.maxPrice')}</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="Any" 
                        {...field}
                        onChange={(e) => field.onChange(e.target.valueAsNumber)} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="urgency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('wants.urgency')}</FormLabel>
                  <Select 
                    onValueChange={field.onChange} 
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select urgency" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="low">{t('wants.urgencyLow')}</SelectItem>
                      <SelectItem value="medium">{t('wants.urgencyMedium')}</SelectItem>
                      <SelectItem value="high">{t('wants.urgencyHigh')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="mt-6">
              <Button 
                type="submit" 
                disabled={form.formState.isSubmitting}
                className="w-full"
              >
                {form.formState.isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common.saving')}
                  </>
                ) : (
                  t('wants.createButton')
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
